import React, { useRef, useState, useEffect } from "react";
import styles from "./Players.module.scss";
import TinySlider from "tiny-slider-react";
import SliderControls from "./SliderButton";
import sliderStyles from "./TinySlider.module.scss";
import { Context } from "../Store";
import { trackEvent } from "../utils/helpers";
import useMediaQuery from "../hooks/useMediaQuery";

function getPlayerSplit(count, isMobile) {
  // Mobile 2–3: keep existing layout
  if (isMobile) {
    if (count <= 2) return { topCount: count, bottomCount: 0, direct: false };
    if (count === 3) return { topCount: 2, bottomCount: 1, direct: false };
    if (count === 4) return { topCount: 2, bottomCount: 2, direct: true };
  }
  // Desktop rules (also used for mobile 4+)
  if (count <= 4) return { topCount: count, bottomCount: 0, direct: false };
  if (count === 5) return { topCount: 3, bottomCount: 2, direct: false };
  if (count === 6) return { topCount: 3, bottomCount: 3, direct: true };
  if (count === 7) return { topCount: 4, bottomCount: 3, direct: false };
  if (count === 8) return { topCount: 4, bottomCount: 4, direct: true };
  if (count === 9) return { topCount: 5, bottomCount: 4, direct: false };
  if (count === 10) return { topCount: 5, bottomCount: 5, direct: true };
  return { topCount: Math.ceil(count / 2), bottomCount: Math.floor(count / 2), direct: false };
}

const Players = (props) => {
  const state = React.useContext(Context);

  function getNumberWithOrdinal(n) {
    var s = ["th", "st", "nd", "rd"],
      v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  const isMobile = useMediaQuery("(max-width: 639px)");
  const noTeams = state.teams.length === 0 ? true : false;
  const teamSliderRef = [];
  teamSliderRef[0] = useRef();
  teamSliderRef[1] = useRef();
  const noTeamsScrollRef = useRef();
  const noTeamsIsDragging = useRef(false);
  const noTeamsDragStart = useRef({ x: 0, scrollLeft: 0 });

  // Start centred when there are more than 3 players so users can scroll either direction
  useEffect(() => {
    if (noTeams && state.players.length > 3 && noTeamsScrollRef.current) {
      requestAnimationFrame(() => {
        const el = noTeamsScrollRef.current;
        if (el) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      });
    }
  }, [noTeams, state.players.length]);
  const hasTrackedScrollRef = useRef(false);
  const playerSettings = [];
  let twoRowPlayers = false;

  for (let [i, team] of state.teams.entries()) {
    let playerCOunt = [...state.players].filter((player) => {
      return player.team_id == team.id;
    }).length;
    twoRowPlayers = playerCOunt > 4 ? true : twoRowPlayers;
    playerSettings[i] = {
      lazyload: true,
      touch: true,
      nav: false,
      controlsContainer: "." + styles.playersContainer + " .sliderControls.multiSlider" + i,
      mouseDrag: true,
      loop: false,
      swipeAngle: 45,
      items: 2,
      gutter: 0,
      edgePadding: 0,
      responsive: {
        480: {
          items: 3,
        },
        640: {
          items: 2,
        },
        1024: {
          items: 3,
        },
        1248: {
          //items: 4 ,
          items: playerCOunt < 4 ? playerCOunt : 4,
        },
      },
    };
  }
  let tournamentSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controlsContainer: "." + styles.playersContainer + " .sliderControls.tournamentSliderControls",
    mouseDrag: true,
    loop: false,
    swipeAngle: 45,
    items: 1,
    gutter: 0,
    slideBy: 1,
    responsive: {
      640: {
        items: 2,
        edgePadding: 20,
      },
      1024: {
        items: 2,
        edgePadding: 0,
      },
    },
  };
  let noTeamsSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controlsContainer: "." + styles.playersContainer + " .sliderControls.tournamentSliderControls",
    mouseDrag: true,
    loop: false,
    swipeAngle: 45,
    fixedWidth: 155,
    gutter: 24,
    slideBy: 1,
    responsive: {
      1248: {
        fixedWidth: 217,
        gutter: 60,
      },
    },
  };

  // Helper function to get player photo
  const getPlayerPhoto = (player) => {
    return player.photo
      ? "data:image/jpeg;base64, " + player.photo
      : player.gender === "male"
        ? "./img/boy.png"
        : "./img/girl.png";
  };

  const scrollToLeaderboard = () => {
    const el = document.querySelector('.lbSection, [class*="lbSection"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const Points = (props) => {
    return (
      <div className={`team_${props.teamColour} ${styles.points}`}>
        <div className={styles.pointsHeight}></div>
        {!noTeams && props.player.points}
        {noTeams && props.player.points * 2 + props.player.draws - props.player.losses}
      </div>
    );
  };
  const generatePlayerComponent = (player, teamColour, className = styles.playerItem) => {
    let playerPhoto = getPlayerPhoto(player);
    return (
      <div key={player.id} className={className}>
        <div className={styles.player}>
          <div className={`team_${teamColour} ${styles.playerImage}`}>
            <img src={playerPhoto} alt="" />
          </div>
          <Points player={player} teamColour={teamColour} />
        </div>
        <div className={styles.playerName}>{player.name.toLowerCase()}</div>
      </div>
    );
  };

  let teams = [];

  if (state.teams.length > 2) {
    teams = [...state.teams]
      .sort((a, b) => b.finalist - a.finalist || a.final_result - b.final_result || b.points - a.points)
      .map((team, index) => {
        let teamColour = team.colour == 1 ? "blue" : "pink";
        let teamPlayerArray = [...state.players].filter((player, origIndex) => {
          player.origIndex = origIndex;
          return player.team_id == team.id;
        });
        let teamPlayers = teamPlayerArray
          .sort((a, b) => b.points - a.points)
          .map((player, index) => {
            let teamLength = twoRowPlayers ? "players_" + teamPlayerArray.length : "onerow";
            return generatePlayerComponent(player, teamColour, [styles.playerItem, styles[teamLength]].join(" "));
          });
        if (teamPlayers.length > 0) {
          return (
            <div key={team.id} className={`team_${teamColour} ${styles.tournamentTeam}`}>
              <div key={index} className={styles.teamTitle}>
                <div className={`team_${team.name.toLowerCase()} ${styles.teamName}`}>{team.name.toLowerCase()}</div>
                <div className={`team_${team.name.toLowerCase()} ${styles.teamPoints}`}>{team.points}</div>
              </div>
              <div className={styles.teamPlayers}>{teamPlayers}</div>
            </div>
          );
        } else {
          return false;
        }
      });
  } else if (state.teams.length > 0) {
    teams = [...state.teams].map((team, index) => {
      let teamPlayers = [...state.players]
        .filter((player, origIndex) => {
          player.origIndex = origIndex;
          return player.team_id == team.id;
        })
        .sort((a, b) => b.points - a.points)
        .map((player, index) => {
          let teamColour = ["pink", "blue"].includes(team.name.toLowerCase())
            ? team.name.toLowerCase()
            : team.colour == 1
              ? "blue"
              : "pink";
          return generatePlayerComponent(player, teamColour);
        });
      if (teamPlayers.length > 0) {
        return (
          // <div  key={team.id} className={styles.teamItem}>
          <div key={team.id} className={styles.playerSlider}>
            <SliderControls slider={teamSliderRef[index]} additionalClass={"multiSlider" + index}></SliderControls>
            <TinySlider
              settings={playerSettings[index]}
              ref={teamSliderRef[index]}
              onIndexChanged={() => {
                if (!hasTrackedScrollRef.current) {
                  hasTrackedScrollRef.current = true;
                  trackEvent("Gallery Scroll", "team_scroll");
                }
              }}
            >
              {teamPlayers}
            </TinySlider>
          </div>
          // </div>
        );
      } else {
        return false;
      }
    });
  } else {
    teams = [...state.players]
      .map((player, index) => {
        player.origIndex = index;
        return player;
      })
      .sort((a, b) => b.points * 2 + b.draws - b.losses - (a.points * 2 + a.draws - a.losses))
      .map((player, index) => {
        return generatePlayerComponent(player, "purple");
      });
  }

  let TeamPoints = () => {
    let points = [];
    state.teams.map((team, index) => {
      let teamColour = ["pink", "blue"].includes(team.name.toLowerCase())
        ? team.name.toLowerCase()
        : team.colour == 1
          ? "blue"
          : "pink";
      points.push(
        <div key={index} className={styles.teamTitle}>
          <div className={`team_${teamColour} ${styles.teamName}`}>{team.name.toUpperCase()}</div>
          <div className={`team_${teamColour} ${styles.teamPoints}`}>{team.points}</div>
        </div>,
      );
    });
    return points;
  };

  const PlayerView = () => {
    let playerView;
    if (noTeams) {
      const { topCount, direct } = getPlayerSplit(teams.length, isMobile);
      const topPlayers = teams.slice(0, topCount);
      const bottomPlayers = teams.slice(topCount);
      const topRowClass =
        teams.length === 2 ? `${styles.noTeamsTopRow} ${styles.noTeamsTopRowTwo}` : styles.noTeamsTopRow;
      const bottomRowClass = direct
        ? `${styles.noTeamsBottomRow} ${styles.noTeamsBottomRowDirect}`
        : styles.noTeamsBottomRow;
      playerView = (
        <div className={styles.noTeamsStaggerWrapper}>
          <div
            className={styles.noTeamsScrollTrack}
            ref={noTeamsScrollRef}
            onPointerDown={(e) => {
              noTeamsIsDragging.current = true;
              noTeamsDragStart.current = { x: e.clientX, scrollLeft: noTeamsScrollRef.current.scrollLeft };
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (!noTeamsIsDragging.current) return;
              e.preventDefault();
              noTeamsScrollRef.current.scrollLeft =
                noTeamsDragStart.current.scrollLeft - (e.clientX - noTeamsDragStart.current.x);
            }}
            onPointerUp={() => {
              noTeamsIsDragging.current = false;
            }}
          >
            <div className={styles.noTeamsRowsWrapper}>
              <div className={topRowClass}>{topPlayers}</div>
              {bottomPlayers.length > 0 && <div className={bottomRowClass}>{bottomPlayers}</div>}
            </div>
          </div>
          <div
            className={styles.noTeamsPrev}
            onClick={() => noTeamsScrollRef.current?.scrollBy({ left: -(155 + 24), behavior: "smooth" })}
          >
            <img src="./img/fc_icon-arrow-prev.svg" alt="previous" />
          </div>
          <div
            className={styles.noTeamsNext}
            onClick={() => noTeamsScrollRef.current?.scrollBy({ left: 155 + 24, behavior: "smooth" })}
          >
            <img src="./img/fc_icon-arrow-next.svg" alt="next" />
          </div>
        </div>
      );
    } else if (state.teams.length > 2) {
      playerView = (
        <div className={styles.tournamentSlider}>
          <SliderControls slider={teamSliderRef[0]} additionalClass={"tournamentSliderControls"}></SliderControls>
          <TinySlider
            settings={noTeams ? noTeamsSettings : tournamentSettings}
            ref={teamSliderRef[0]}
            onIndexChanged={() => {
              if (!hasTrackedScrollRef.current) {
                hasTrackedScrollRef.current = true;
                trackEvent("Gallery Scroll", "team_scroll");
              }
            }}
          >
            {teams}
          </TinySlider>
        </div>
      );
    } else {
      playerView = <div className={styles.playerSliderContainer}>{teams}</div>;
    }
    return playerView;
  };

  let playerPhotoSources = state.players.map((player) => {
    return "data:image/jpeg;base64," + player.photo;
  });
  let playerPhotoMedia = state.players.map((player) => {
    return {
      mediaType: "PHOTO",
      mediaShare: state.guid + "&player=" + player.id,
      mediaURI: "data:image/jpeg;base64," + player.photo,
      sectionName: "Player Photo",
    };
  });

  const openLightbox = (index) => {
    let lightboxDetails = {
      section: "Players",
      index: index,
      sources: playerPhotoSources,
      mediashare: playerPhotoMedia,
      type: "image",
    };
    props.openLightbox(lightboxDetails);
  };

  //let playerView = teams ;
  let stylesSection = noTeams
    ? `team_section no_teams ${styles.team_container}`
    : state.teams.length < 3
      ? `teams_section player_slider_section ${styles.team_container}`
      : `teams_section ${styles.team_container}`;

  return (
    <div className={`section light ${stylesSection}`}>
      {noTeams && (
        <div className={styles.playerImages}>
          <img src="./img/drawing-bolt.svg" alt="bolt" className={styles.bolt} />
          <img src="./img/drawing-disco-ball.svg" alt="disco ball" className={styles.discoBall} />
        </div>
      )}
      {state.teams.length > 2 && <h2>The teams</h2>}
      {noTeams && (
        <>
          <div className={`${styles.teamTitle} ${styles.noTeamsTitle}`}>
            {state.players.length === 2 ? "The duel" : "The team"}
          </div>
        </>
      )}
      <div className="">
        {state.teams.length < 3 && (
          <div className={styles.pointsContainer}>
            <TeamPoints />
          </div>
        )}
        <div className={styles.playersContainer}>
          <PlayerView />
        </div>
      </div>
      <div className={`${styles.viewMore}`} onClick={scrollToLeaderboard} style={{ cursor: "pointer" }}>
        View leaderboard
      </div>{" "}
      {noTeams && (
        <div className={styles.playerImagesBottomMorePlayers}>
          <img src="./img/drawing-confetti.svg" alt="confetti" className={styles.confetti} />
          <img src="./img/drawing-stars.svg" alt="stars" className={styles.stars} />
        </div>
      )}
    </div>
  );
};

export default Players;
